import { useEffect, useState } from "react";
import api from "../../lib/api";

/**
 * <VehiclePicker /> — extracted from JobCardDetail.jsx (Wave 25.16
 * Phase 2 refactor). Vehicle picker with overlap-conflict warning.
 *
 * Props:
 *   value      — vehicle id (string) | null
 *   startsAt   — ISO scheduled_at
 *   endsAt     — ISO ends_at | null
 *   jobcardId  — current JC id, excluded from conflict check
 *   canEdit    — bool
 *   onChange   — (id|null) => void
 */
export default function VehiclePicker({ value, startsAt, endsAt, jobcardId, canEdit, onChange }) {
  const [vehicles, setVehicles] = useState([]);
  const [conflicts, setConflicts] = useState({}); // vid → list of jobcards
  const [override, setOverride] = useState(false);

  // Load vehicles once.
  useEffect(() => {
    api.get("/vehicles").then(({ data }) => setVehicles(data || [])).catch(() => setVehicles([]));
  }, []);

  // For every vehicle, check whether it's already booked in this window.
  // We do these in parallel — typical fleet size is 3–10, so fine.
  useEffect(() => {
    if (!startsAt) {
      setConflicts({});
      return;
    }
    let cancelled = false;
    Promise.all(
      vehicles.map((v) =>
        api
          .get("/vehicles/conflicts", {
            params: {
              vehicle_id: v.id,
              starts: startsAt,
              ends: endsAt || undefined,
              exclude_jobcard_id: jobcardId || undefined,
            },
          })
          .then((r) => [v.id, r.data])
          .catch(() => [v.id, []]),
      ),
    ).then((rows) => {
      if (cancelled) return;
      const out = {};
      for (const [vid, list] of rows) out[vid] = list || [];
      setConflicts(out);
    });
    return () => {
      cancelled = true;
    };
  }, [vehicles, startsAt, endsAt, jobcardId]);

  if (!canEdit) {
    const v = vehicles.find((x) => x.id === value);
    return (
      <div className="hv-input mt-1 opacity-70 flex items-center gap-2">
        {v ? (
          <>
            <span
              className="w-2 h-2 rounded-full"
              style={{ background: v.color || "#1fb6c1" }}
            />
            {v.name}
          </>
        ) : (
          "—"
        )}
      </div>
    );
  }

  const selectedConflicts = (value && conflicts[value]) || [];
  const hasConflict = selectedConflicts.length > 0;

  return (
    <div className="space-y-1.5">
      <select
        value={value || ""}
        onChange={(e) => {
          const v = e.target.value;
          if (!v) {
            onChange(null);
            setOverride(false);
            return;
          }
          onChange(v);
        }}
        className="hv-input mt-1"
        data-testid="job-vehicle-select"
      >
        <option value="">— No vehicle —</option>
        {vehicles.map((v) => {
          const conflict = (conflicts[v.id] || []).length > 0;
          return (
            <option
              key={v.id}
              value={v.id}
              style={{ color: conflict ? "#fda4af" : "#34d399" }}
            >
              {v.name}
              {conflict ? "  ·  ⚠ booked" : "  ·  available"}
            </option>
          );
        })}
      </select>
      {hasConflict && (
        <div
          className="text-[11px] border border-rose-400/40 bg-rose-500/10 text-rose-200 rounded p-2 space-y-1"
          data-testid="job-vehicle-conflict-warning"
        >
          <div className="font-bold">⚠ This vehicle overlaps with:</div>
          <ul className="list-disc list-inside opacity-90">
            {selectedConflicts.slice(0, 3).map((c) => (
              <li key={c.id}>
                {c.title || c.short_id || c.id?.slice(0, 8)} —{" "}
                {c.scheduled_at?.slice(0, 16).replace("T", " ")}
              </li>
            ))}
          </ul>
          <label className="flex items-center gap-1.5 mt-1 cursor-pointer">
            <input
              type="checkbox"
              checked={override}
              onChange={(e) => setOverride(e.target.checked)}
              data-testid="job-vehicle-override"
              className="accent-rose-400"
            />
            <span>Use anyway (I've coordinated with the other crew)</span>
          </label>
        </div>
      )}
    </div>
  );
}
